import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { LogOut, Menu, X } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

export const Navbar: React.FC = () => {
  const location = useLocation();
  const isAdmin = location.pathname.includes('/admin');
  const { t } = useLanguage();

  const [mobileOpen, setMobileOpen] = useState(false);

  // Close the mobile menu on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // Prevent background scroll while mobile menu is open
  useEffect(() => {
    if (!mobileOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [mobileOpen]);

  const isActive = (to: string) => {
    if (to === '/') return location.pathname === '/';
    return location.pathname.startsWith(to);
  };


  return (
    <nav className="w-full bg-white/80 backdrop-blur-md border-b border-brand-100 sticky top-0 z-50 transition-all duration-300 relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-20 items-center">
          <Link to="/" className="flex items-center gap-2 group transform active:scale-95 transition-transform">
             <div className="w-8 h-8 bg-brand-900 rounded-tr-xl rounded-bl-xl flex items-center justify-center shadow-lg group-hover:bg-brand-800 transition-colors">
                <span className="text-white font-serif font-bold text-xl">C</span>
             </div>
             <span className="font-serif text-2xl font-bold text-brand-900 tracking-tight transition-colors">Chalet Inn</span>
          </Link>
          
          <div className="hidden md:flex space-x-8 items-center">
             <Link to="/" className="text-brand-800 hover:text-brand-600 font-medium transition-colors text-sm uppercase tracking-wide">{t('nav.home')}</Link>
             <Link to="/overview" className="text-brand-800 hover:text-brand-600 font-medium transition-colors text-sm uppercase tracking-wide">{t('nav.overview')}</Link>
             <Link to="/blog" className="text-brand-800 hover:text-brand-600 font-medium transition-colors text-sm uppercase tracking-wide">Blog</Link>
             <Link to="/verhuren" className="text-brand-800 hover:text-brand-600 font-medium transition-colors text-sm uppercase tracking-wide">{t('nav.rent')}</Link>
             <Link to="/contact" className="text-brand-800 hover:text-brand-600 font-medium transition-colors text-sm uppercase tracking-wide">{t('nav.contact')}</Link>
          </div>

			
          <div className="flex items-center gap-3">
		  {isAdmin && (
			<Link
			  to="/"
			  className="px-4 py-2 bg-brand-100 text-brand-900 rounded-full text-xs font-bold uppercase tracking-wider flex items-center gap-2 hover:bg-brand-200 transition-colors"
			>
			  <LogOut size={14} /> {t('nav.exit')}
			</Link>
		  )}

          {/* Mobile menu button */}
          <button
            type="button"
            onClick={() => setMobileOpen(o => !o)}
            className="md:hidden inline-flex items-center justify-center w-11 h-11 rounded-2xl border border-brand-100 bg-white/70 backdrop-blur hover:bg-brand-50 transition-colors"
            aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileOpen}
            aria-controls="mobile-nav"
          >
            {mobileOpen ? <X size={18} /> : <Menu size={18} />}
          </button>
          </div>
			
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <>
          {/* Overlay */}
          <button
            type="button"
            aria-label="Close menu overlay"
            onClick={() => setMobileOpen(false)}
            className="fixed inset-0 top-20 bg-black/20 md:hidden z-40"
          />

          {/* Panel */}
          <div
            id="mobile-nav"
            className="absolute top-full inset-x-0 md:hidden bg-white/95 backdrop-blur-md border-b border-brand-100 shadow-xl z-50"
          >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
              <div className="grid gap-2">
                <Link
                  to="/"
                  onClick={() => setMobileOpen(false)}
                  className={`px-4 py-3 rounded-2xl font-semibold transition-colors ${
                    isActive('/')
                      ? 'bg-brand-50 text-brand-900'
                      : 'text-brand-800 hover:bg-brand-50 hover:text-brand-900'
                  }`}
                >
                  {t('nav.home')}
                </Link>
                <Link
                  to="/overview"
                  onClick={() => setMobileOpen(false)}
                  className={`px-4 py-3 rounded-2xl font-semibold transition-colors ${
                    isActive('/overview')
                      ? 'bg-brand-50 text-brand-900'
                      : 'text-brand-800 hover:bg-brand-50 hover:text-brand-900'
                  }`}
                >
                  {t('nav.overview')}
                </Link>
                <Link
                  to="/blog"
                  onClick={() => setMobileOpen(false)}
                  className={`px-4 py-3 rounded-2xl font-semibold transition-colors ${
                    isActive('/blog')
                      ? 'bg-brand-50 text-brand-900'
                      : 'text-brand-800 hover:bg-brand-50 hover:text-brand-900'
                  }`}
                >
                  Blog
                </Link>
                <Link
                  to="/verhuren"
                  onClick={() => setMobileOpen(false)}
                  className={`px-4 py-3 rounded-2xl font-semibold transition-colors ${
                    isActive('/verhuren')
                      ? 'bg-brand-50 text-brand-900'
                      : 'text-brand-800 hover:bg-brand-50 hover:text-brand-900'
                  }`}
                >
                  {t('nav.rent')}
                </Link>
                <Link
                  to="/contact"
                  onClick={() => setMobileOpen(false)}
                  className={`px-4 py-3 rounded-2xl font-semibold transition-colors ${
                    isActive('/contact')
                      ? 'bg-brand-50 text-brand-900'
                      : 'text-brand-800 hover:bg-brand-50 hover:text-brand-900'
                  }`}
                >
                  {t('nav.contact')}
                </Link>
              </div>
            </div>
          </div>
        </>
      )}
    </nav>
  );
};